import React, { useEffect, useRef, useState } from 'react'
import { Accordion, Button, Header, Icon, Label, Segment, Table, Transition } from 'semantic-ui-react';
import { StyledAccordionTitle, StyledNoResultsMessage } from '../style';
import notify from '~/components/Notify';
import { useTranslation } from 'react-i18next';
import { PriceCalculatorService } from '~/api/services/priceCalculator';
import { capitalizeFirstLetter, formatNumber, handleErrors } from '~/utils/helpers';
import dayjs from 'dayjs';
import { ADMIN_ROLE_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants';
import { useAuth } from '~/routes/components/AuthProvider';
import CustomInput from '~/components/CustomInput';
import { MilkTypeWrapper } from './style';
import Dialog from '~/components/Dialog';
import TwoFaComp from '~/components/TwoFaComp';
import ProfileSettingsService from '~/api/services/profileSettings';
import cloneDeep from 'lodash.clonedeep'

const priceTypeKeys = ['price', 'fat', 'protein']

const MilkTypes: React.FC = () => {
  const { t } = useTranslation()
  const { systemData, systemData: { logged: { roleSlug } } } = useAuth()
  const [activeAccordionIndex, setActiveAccordionIndex] = useState(-1)
  const [milkTypes, setMilkTypes] = useState([])
  const [months, setMonths] = useState([])
  const [isBusy, setIsBusy] = useState(false)
  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const tfaType = systemData?.logged?.tfaType
  const [tfaCode, setTfaCode] = useState('')
  const tfaPublishBasePrice = systemData?.roleSettings?.tfaPublishBasePrice === 1
  const [openTwoFaModal, setOpenTwoFaModal] = useState(false)
  const editPermission = ADMIN_ROLE_SLUG === roleSlug || OPERATIVE_ROLE_SLUG === roleSlug
  const [error, setError] = useState(false)
  const originalMonthsRef = useRef([]);

  useEffect(() => {
    fetchMilkTypeData()
  }, [])

  async function fetchMilkTypeData() {
    setIsBusy(true)
    try {
      const response = await PriceCalculatorService.getMilkTypesPricesOptions()
      setMilkTypes(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  useEffect(() => {
    if (activeAccordionIndex > -1) {
      fetchMonths()
    }
  }, [activeAccordionIndex])

  async function fetchMonths() {
    setIsBusy(true)
    try {
      const milkTypeId = milkTypes[activeAccordionIndex]?.milkTypeId
      const response = await PriceCalculatorService.getMilkTypesMonths({ milkTypeId })
      setMonths(response.data)
      originalMonthsRef.current = response.data
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function handleMilkTypeClick(e, titleProps) {
    const { index } = titleProps
    setActiveAccordionIndex(activeAccordionIndex === index ? -1 : index);
  }

  function renderNoResultsMessage() {
    return <StyledNoResultsMessage>
      <Segment placeholder>
        <Header icon>
          <Icon name='search' />
          {t('No results found....')}
        </Header>
        <Segment.Inline>
        </Segment.Inline>
      </Segment>
    </StyledNoResultsMessage>
  }

  function handleChangeInput(value, index, key) {
    const monthsTemp = cloneDeep(months)
    monthsTemp[index][key] = value
    setMonths(monthsTemp)
  }

  useEffect(() => {
    const errorElements = document.getElementsByClassName('ui input error');
    setError(errorElements.length > 0)
  }, [months]);

  function renderTableBody() {
    return months.map((month, index) => {
      return <Table.Row key={month.data ?? Math.random()}>
        <Table.Cell><Label ribbon>{capitalizeFirstLetter(dayjs(month?.date).format('MMM YYYY'))}</Label></Table.Cell>
        <Table.Cell>
          <CustomInput
            value={month.price}
            allowDecimals
            limit={99.99}
            min={0.01}
            disabled={!editPermission}
            onChange={(value) => handleChangeInput(value, index, 'price')}
            style={{ width: '100%' }}
            placeholder={t('Min: 0,01  Max: 99,99')}
          />
        </Table.Cell>
        <Table.Cell>
          <CustomInput
            value={month.fat}
            limit={9.99}
            allowDecimals
            min={0.01}
            disabled={!editPermission}
            onChange={(value) => handleChangeInput(value, index, 'fat')}
            style={{ width: '100%' }}
            placeholder={t('Min: 0,01 Max: 9,99')}
          />
        </Table.Cell>
        <Table.Cell>
          <CustomInput
            value={month.protein}
            limit={9.99}
            allowDecimals
            min={0.01}
            disabled={!editPermission}
            onChange={(value) => handleChangeInput(value, index, 'protein')}
            style={{ width: '100%' }}
            placeholder={t('Min: 0,01 Max: 9,99')}
          />
        </Table.Cell>
      </Table.Row>
    })
  }

  function renderMilkTypeTable() {
    return <Table celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{t('Date')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Price (€ Ct/kg)')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Fat (in %)')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Protein (in %)')}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {months.length > 0 ? renderTableBody() : <Table.Row>
          <Table.Cell colSpan={2}>
            {renderNoResultsMessage()}
          </Table.Cell>
        </Table.Row> }
      </Table.Body>
    </Table>
  }

  async function handleSendCode() {
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  function renderMilkTypes() {
    const tempMonths = cloneDeep(months)
    const changedMonths = originalMonthsRef?.current.length > 0 ? tempMonths.reduce((previousMontshChanged, month, index) => {
      const originalMonth = originalMonthsRef?.current.length > 0 ? originalMonthsRef?.current[index] : null;
      priceTypeKeys.map(priceTypeKey => {
        month[priceTypeKey] = month[priceTypeKey] ? month[priceTypeKey].replace(',', '.') : month[priceTypeKey]
      })
      if (month?.price && (
        month?.price !== originalMonth?.price ||
        month?.fat !== originalMonth?.fat ||
        month?.protein !== originalMonth?.protein
      )
      ) {
        previousMontshChanged.push(month)
      }
      return previousMontshChanged
    }, []) : []

    return milkTypes?.map((milkType, index) => {
      return <Accordion
        key={milkType?.id + index}
        styled
        style={{ width: 'calc(100% - 40px)', margin: 10 }}
      >
        <Accordion.Title
          onClick={handleMilkTypeClick}
          index={index}
          active={activeAccordionIndex === index}
          style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <StyledAccordionTitle>{`${milkType?.name} (${milkType?.abbreviation})`}</StyledAccordionTitle>
          <Icon name={activeAccordionIndex === index ? 'minus' : 'plus'} />
        </Accordion.Title>
        <Transition
          visible={activeAccordionIndex === index && !isBusy}
          animation='slide down'
          duration={200}
        >
          <Accordion.Content
            active={activeAccordionIndex === index}
            content={!isBusy && <MilkTypeWrapper>
              <div className='actions'>
                <Button
                  onClick={() => {
                    if (tfaPublishBasePrice) {
                      setOpenTwoFaModal(true)
                      handleSendCode()
                    } else {
                      handleSubmitPrice()
                    }
                  }}
                  primary
                  disabled={(changedMonths.length === 0) || (!editPermission || error)}
                >
                  {t('Update')}
                </Button>
              </div>
              {renderMilkTypeTable()}
            </MilkTypeWrapper>}
          />
        </Transition>
      </Accordion>
    })
  }

  async function handleSubmitPrice() {
    const milkType = activeAccordionIndex >= 0 ? milkTypes[activeAccordionIndex] : ''
    setIsBusy(true)
    try {
      const monthPriceChanged = months.filter(month => !!month.price)
      monthPriceChanged.forEach(month => {
        priceTypeKeys.map(priceTypeKey => {
          month[priceTypeKey] = month[priceTypeKey]?.replace(',', '.')
        })
      })
      const milkTypeId = milkTypes[activeAccordionIndex]?.milkTypeId
      await PriceCalculatorService.updateMilkTypesMonths({
        months: monthPriceChanged,
        milkTypeId,
        code: tfaCode
      })
      if (tfaPublishBasePrice) {
        handleCloseModal()
      }
      notify(t('The base price and values ​​for organic milk {{name}} have been successfully updated.', { name: `${milkType?.name} (${milkType?.abbreviation})` }), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function handleCloseModal() {
    setTfaCode('')
    setCorrectTfaCode(false)
    setOpenTwoFaModal(false)
  }

  function renderChangedPriceBody(changedMonths) {
    return changedMonths.map(changedMonth => {
      return <Table.Row key={`changedMonth-${changedMonth?.date}`}>
        <Table.Cell><Label ribbon>{capitalizeFirstLetter(dayjs(changedMonth?.date).format('MMM YYYY'))}</Label></Table.Cell>
        <Table.Cell>{formatNumber(changedMonth?.price, 2)}</Table.Cell>
        <Table.Cell>{formatNumber(changedMonth?.fat, 2)}</Table.Cell>
        <Table.Cell>{formatNumber(changedMonth?.protein, 2)}</Table.Cell>
      </Table.Row>
    })
  }

  function renderTableChangedPrices() {
    const changedMonths = months?.filter((month, index) => {
      const originalMonth = originalMonthsRef?.current[index];
      priceTypeKeys.map(priceTypeKey => {
        month[priceTypeKey] = month[priceTypeKey] ? month[priceTypeKey].replace(',', '.') : month[priceTypeKey]
      })
      return month?.price && (
        month?.price !== originalMonth?.price ||
        month?.fat !== originalMonth?.fat ||
        month?.protein !== originalMonth?.protein
      );
    });

    return <>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t('Date')}</Table.HeaderCell>
            <Table.HeaderCell>{t('Price (€ Ct/kg)')}</Table.HeaderCell>
            <Table.HeaderCell>{t('Fat (in %)')}</Table.HeaderCell>
            <Table.HeaderCell>{t('Protein (in %)')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {changedMonths.length > 0 && renderChangedPriceBody(changedMonths)}
        </Table.Body>
      </Table>
      {changedMonths.length === 0 && renderNoResultsMessage()}
    </>
  }

  function render2FAMessage(name) {
    return <div>{t('You have entered the following values ​​for organic milk {{name}}, which will have an impact on the milk payment billing:', { name })}</div>
  }

  function renderTwoFaModal() {
    const milkType = activeAccordionIndex >= 0 ? milkTypes[activeAccordionIndex] : ''
    return <Dialog
      isDialogOpen={openTwoFaModal}
      textHeader={t('Update basic price for organic milk {{name}}.', { name: `${milkType?.name} (${milkType?.abbreviation})` })}
      handleClose={() => handleCloseModal()}
      disableSubmit={!correctTfaCode || isBusy}
      textClose={t('Close')}
      textContent={<>
        {render2FAMessage(`${milkType?.name} (${milkType?.abbreviation})`)}
        {renderTableChangedPrices()}
        <TwoFaComp
          onChangeTfaCode={(code) => setTfaCode(code)}
          onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
        />
      </>}
      isLoading={isBusy}
      textSubmit={t('Update')}
      handleSubmit={() => handleSubmitPrice()}
    />
  }

  return <>
    {openTwoFaModal && renderTwoFaModal()}
    {renderMilkTypes()}
  </>
}
export default MilkTypes